import { backendClient } from './api-client';

export interface OutlookSyncState {
  matterGuid?: string;
  isSyncing: boolean;
  batchId?: string;
  lastSyncTime?: number;
  status: 'idle' | 'checking' | 'syncing' | 'completed' | 'failed';
  error?: string;
}

/**
 * Polls Outlook sync status for a given matter
 * Simply calls the backend endpoint which handles all sync logic including:
 * - Redis lock checking for existing syncs
 * - Delta token checking for changes
 * - Starting new syncs if needed
 */
export async function pollOutlookSync(matterGuid?: string): Promise<OutlookSyncState> {
  // If no matter selected, return idle state
  if (!matterGuid) {
    return {
      matterGuid: undefined,
      isSyncing: false,
      status: 'idle',
    };
  }

  try {
    // Call the backend endpoint - it will tell us if sync is needed/in progress
    let syncResponse;
    try {
      syncResponse = await backendClient.get(
        `/correspondence/${matterGuid}/fetch-outlook-updates`
      );
    } catch (error: any) {
      // Handle 429 token limit exceeded error
      if (error.response?.status === 429) {
        // Silently skip matters over token limit
        return {
          matterGuid,
          isSyncing: false,
          status: 'idle',
        };
      }
      // Handle 403 error - stop polling with clear error
      if (error.response?.status === 403) {
        return {
          matterGuid,
          isSyncing: false,
          status: 'failed',
          error: error.response.data.detail.message,
        };
      }
      throw error; // Re-throw other errors
    }

    const result = syncResponse.data;

    // Handle different response types from backend
    if (result.status === 'in_progress') {
      // Backend says sync is already in progress (Redis lock exists)
      return {
        matterGuid,
        isSyncing: true,
        batchId: result.batch_id,
        lastSyncTime: Date.now(),
        status: 'syncing',
      };
    }

    if (result.status === 'no_changes') {
      // No changes detected, nothing to sync
      return {
        matterGuid,
        isSyncing: false,
        lastSyncTime: Date.now(),
        status: 'completed',
      };
    }

    if (result.batch_id && result.status === 'queued') {
      // Backend started a new sync
      return {
        matterGuid,
        isSyncing: true,
        batchId: result.batch_id,
        lastSyncTime: Date.now(),
        status: 'syncing',
      };
    }

    // Default idle state
    return {
      matterGuid,
      isSyncing: false,
      status: 'idle',
    };
  } catch (error: any) {
    console.error('Error polling Outlook sync:', error);

    // Don't treat errors as failures - just return idle state to avoid noise
    return {
      matterGuid,
      isSyncing: false,
      status: 'idle',
      error: error.response?.data?.detail || error.message,
    };
  }
}
